import { Component } from "react"

/* Components */
import { DataLayout, Icon } from "../../components"

/* Widgets */
import { toast, confirm } from "../../widgets/common"

/* Shared */
import { ago } from "../../widgets/user/shared"

/* REST API */
import { get, save } from "../../api/Settings"


/* The languages the Whatsapp messages may go out in */
const LANGUAGES = [
    { value: "ru", text: "Русский", hint: "Все сообщения клиентам и команде на русском" },
    { value: "kk", text: "Қазақша", hint: "Казахский перевод шаблона, а если Meta его ещё не одобрила — русский" }
]


/* Page Settings — the settings of the whole platform. The first one is the language of the
   Whatsapp messages. The template keeps its name in both languages: the Kazakh one is its
   translation in Meta, so a translation which is not approved yet simply gives the Russian
   message, and the table below shows which of them still wait */
class Settings extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: null,
            saving: false
        }
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(true)
    }

    load = (reload = false) => {

        if (reload) {
            this.setState({ status: "loading" })
        }

        get({}).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error", data: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null })
        })
    }

    /* Change the language of the Whatsapp messages */
    language = value => {

        /* Fields */
        const { data, saving } = this.state

        if (saving || !data || data.settings.whatsappLanguage === value) {
            return
        }

        /* How many Kazakh translations Meta has already let through */
        const ready = data.templates.filter(item => item.kk.total - item.kk.failed > 0).length

        confirm({
            title: value === "kk" ? "Перевести WhatsApp на казахский?" : "Вернуть WhatsApp на русский?",
            text: value === "kk"
                ? `Сообщения клиентам и команде пойдут на казахском. Шаблон без казахского перевода в Meta отправится на русском. Уже работают: ${ready} из ${data.templates.length}`
                : "Все сообщения клиентам и команде снова пойдут на русском",
            confirmText: "Да, переключить",
            cancelText: "Нет"
        }).then(confirmed => {

            if (!confirmed) {
                return
            }

            this.setState({ saving: true })

            save({ name: "whatsappLanguage", value }).then(response => {

                this.setState({ saving: false })

                if (response && response.status === 200) {
                    toast.success(value === "kk" ? "WhatsApp теперь на казахском" : "WhatsApp теперь на русском")
                    this.load()
                    return
                }

                toast.error("Что-то пошло не так! Попробуйте позднее")
            }).catch(() => {
                this.setState({ saving: false })
                toast.error("Что-то пошло не так! Попробуйте позднее")
            })
        })
    }

    _header = () => {
        return (
            <div className="organization-header">
                <div className="page-header-text">Настройки</div>
            </div>
        )
    }

    /* The switch of the language */
    _language = () => {

        /* Fields */
        const { data, saving } = this.state
        const current = data.settings.whatsappLanguage

        return (
            <div className="settings-card">

                <div className="settings-card-head">
                    <div className="settings-card-icon"><Icon name="notification" size={20} width={2} /></div>
                    <div className="settings-card-title">
                        <b>Язык WhatsApp-сообщений</b>
                        <span>Для всех точек. У точки можно поставить свой язык в её карточке → Настройки. Очередь и коды входа остаются на русском</span>
                    </div>
                </div>

                <div className="settings-languages">
                    {LANGUAGES.map(item =>
                        <div
                            key={item.value}
                            onClick={() => this.language(item.value)}
                            className={`settings-language ${current === item.value ? "active" : ""} ${saving ? "disabled" : ""}`}
                        >
                            <div className="settings-language-radio">{current === item.value && <Icon name="check" size={13} width={3} />}</div>
                            <div className="settings-language-text">
                                <b>{item.text}</b>
                                <span>{item.hint}</span>
                            </div>
                        </div>
                    )}
                </div>

                {data.changedAt &&
                    <div className="settings-card-note">
                        Изменено {ago(data.changedAt)}{data.changedBy ? ` · ${data.changedBy.name ? data.changedBy.name : data.changedBy.email}` : ""}
                    </div>
                }

                {/* The points with a language of their own — the switch above does not move them */}
                {data.own && data.own.length > 0 &&
                    <div className="settings-own">
                        <b>Свой язык у {data.own.length} {data.own.length === 1 ? "точки" : "точек"}</b>
                        <span>Переключатель выше на них не действует — язык меняется в карточке точки</span>
                        <div className="settings-own-list">
                            {data.own.map(item =>
                                <div key={`${item.id}`} className="settings-own-item">
                                    {item.name}
                                    <em>{item.language === "kk" ? "Қазақша" : "Русский"}</em>
                                </div>
                            )}
                        </div>
                    </div>
                }

            </div>
        )
    }

    /* The state of one Kazakh translation */
    _state = item => {

        /* Fields */
        const delivered = item.kk.total - item.kk.failed

        if (delivered > 0) {
            return <div className="user-badge green"><i />Работает</div>
        }

        if (item.kk.failed > 0) {
            return <div className="user-badge red" title={item.kk.error ? item.kk.error : ""}><i />Нет в Meta</div>
        }

        return <div className="user-badge grey"><i />Не проверен</div>
    }

    /* The templates and how they went out during the last 30 days */
    _templates = () => {

        /* Fields */
        const { data } = this.state

        return (
            <div className="settings-card">

                <div className="settings-card-head">
                    <div className="settings-card-icon"><Icon name="layers" size={20} width={2} /></div>
                    <div className="settings-card-title">
                        <b>Шаблоны за 30 дней</b>
                        <span>Казахский перевод создаётся в Meta под тем же именем, язык — Kazakh (kk). Тексты — docs/whatsapp-templates.md</span>
                    </div>
                </div>

                <div className="settings-table">

                    <div className="settings-row head">
                        <div>Шаблон</div>
                        <div>Русский</div>
                        <div>Казахский</div>
                        <div>Перевод</div>
                    </div>

                    {data.templates.map(item =>
                        <div key={item.name} className="settings-row">
                            <div className="settings-template">
                                <b>{item.text}</b>
                                <span>{item.name} · {item.recipient === "member" ? "команде" : "клиенту"}</span>
                            </div>
                            <div className="settings-number">
                                <b>{item.ru.total}</b>
                                {item.ru.failed > 0 && <span className="failed">{item.ru.failed} с ошибкой</span>}
                            </div>
                            <div className="settings-number">
                                <b>{item.kk.total - item.kk.failed}</b>
                                {item.kk.failed > 0 && <span className="failed">{item.kk.failed} отказ{item.kk.errorAt ? ` · ${ago(item.kk.errorAt)}` : ""}</span>}
                            </div>
                            <div>{this._state(item)}</div>
                        </div>
                    )}

                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data } = this.state

        if (status !== "ok" || !data) {
            return (
                <div className="page-content">
                    <DataLayout status={status} reload={() => this.load(true)} data={data ? [data] : []} />
                </div>
            )
        }

        return (
            <div className="page-content">
                {this._language()}
                {this._templates()}
            </div>
        )
    }

    render = () => {
        return (
            <div className="page">
                {this._header()}
                {this._content()}
            </div>
        )
    }
}

export default Settings
